import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import WifiOffIcon from '@material-ui/icons/WifiOff';

import * as StoreService from '../../api/storeService';
import * as ProfileService from '../../api/profileService';
import * as EmploymentService from '../../api/employmentService';
import useAuthContext from '../../hooks/useAuthContext';
import useNavContext from '../../hooks/useNavContext';
import useSelectedStoreContext from '../../hooks/useSelectedStoreContext';
import useModeTypeContext from '../../hooks/usemodeTypeContext';
import KioskCodeOnlyScreen from '../../components/Screen/KioskCodeOnlyScreen';
import SaveToHomeScreenDialog from '../../components/Dialog/SaveToHomeScreenDialog';
import SelectStoreDialog from '../../components/Dialog/SelectStoreDialog';
import Spinner from '../../components/Spinner';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
  grow: {
    flexGrow: 1,
  },
  noNetworkContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    width: '100vw',
  },
  noNetworkHeader: {
    marginBottom: theme.spacing(),
  },
  noNetworkSubText: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 16,
    textAlign: 'center',
  },
  wifiOff: {
    width: 150,
    height: 150,
  },
}));

/**
 * Kiosk Mode screen
 */
export default function KioskMode() {
  const classes = useStyles();
  const selectedStore = useSelectedStoreContext();
  const mode = useModeTypeContext();
  const [saveToHomeDialog, setSaveToHomeDialog] = useState(false);
  const [selectStoreDialogOpen, setSelectStoreDialogOpen] = useState(
    !selectedStore?.selectedStore?.storeId,
  );
  const [profile, setProfile] = useState<ProfileService.Profile | null>(null);
  const [employment, setEmployment] =
    useState<EmploymentService.Employment | null>(null);
  const [init, setInit] = useState(true);
  const [hasBootstrapError, setHasBootstrapError] = useState(false);
  const auth = useAuthContext();
  const nav = useNavContext();
  const history = useHistory();

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await ProfileService.get();
        setProfile(res);
        setHasBootstrapError(false);
      } catch (e) {
        console.log('Error: ', e);
        setHasBootstrapError(true);
      }
    };

    fetch();
  }, []);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await EmploymentService.get();
        if (res) {
          setEmployment(res);
          setHasBootstrapError(false);
          setInit(false);
        }
      } catch (e) {
        console.log('Error:', e);
        setHasBootstrapError(true);
        setInit(false);
      }
    };
    if (init) {
      fetch();
    }
  }, [init]);

  useEffect(() => {
    if (!selectedStore?.selectedStore?.storeId) {
      setSelectStoreDialogOpen(true);
    }
  }, [selectedStore]);

  const handleLogOut = async () => {
    auth?.removeSession();
    nav?.removeNav();
    selectedStore?.removeSelectedStore();
    mode?.removeMode();
    history.replace({});
    history.push(`/`);
  };

  const handleRetry = () => {
    window.location.reload();
  };

  if (hasBootstrapError) {
    return (
      <div className={classes.noNetworkContainer}>
        <Typography variant="h5" className={classes.noNetworkHeader}>
          Network Error
        </Typography>
        <WifiOffIcon color="disabled" className={classes.wifiOff} />
        <Typography className={classes.noNetworkSubText}>
          Please check your connection and tap REFRESH to try again.
        </Typography>
        <Button variant="contained" color="primary" onClick={handleRetry}>
          REFRESH
        </Button>
      </div>
    );
  }

  if (init) {
    return <Spinner />;
  }

  if (!profile) {
    return <Spinner />;
  }

  if (!employment) {
    return <Spinner />;
  }

  return (
    <>
      <div className={classes.grow}>
        <div className={classes.container}>
          {selectedStore &&
            selectedStore.selectedStore &&
            selectedStore.selectedStore.storeId &&
            profile && (
              <KioskCodeOnlyScreen
                onLogout={() => {
                  handleLogOut();
                }}
                storeId={selectedStore.selectedStore.storeId}
                onlyScanOnce={true}
                storeName={selectedStore.selectedStore.storeName}
              />
            )}
          <SelectStoreDialog
            employment={employment}
            profile={profile}
            open={selectStoreDialogOpen}
            onClose={() => {
              setSelectStoreDialogOpen(false);
              setSaveToHomeDialog(true);
            }}
            onSuccess={(store: StoreService.Store) => {
              selectedStore?.setSelectedStore({
                storeId: store.id,
                storeName: store.name,
              });
              setSelectStoreDialogOpen(false);
              setSaveToHomeDialog(true);
            }}
          />
          <SaveToHomeScreenDialog
            open={saveToHomeDialog}
            onClose={() => {
              setSaveToHomeDialog(false);
            }}
          />
        </div>
      </div>
    </>
  );
}
